const API_BASE_URL =
  process.env.REACT_APP_API_URL || "/api";

// Helper function for making API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  // Add JWT token if available
  const token = localStorage.getItem("admin_token");
  if (token) {
    defaultOptions.headers.Authorization = `Bearer ${token}`;
  }

  const config = { ...defaultOptions, ...options };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API Error: ${response.status}`);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) return {};

    return JSON.parse(text);
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// Questions API
export const questionsAPI = {
  // Get all questions
  getAll: () => apiCall("/questions"),

  // Get questions by test type
  getByType: (testType) => apiCall(`/questions/${testType}`),

  // Add a single question
  add: (testType, question) =>
    apiCall(`/questions/${testType}`, {
      method: "POST",
      body: JSON.stringify(question),
    }),

  // Update a question (will create endpoint)
  update: (questionId, question) =>
    apiCall(`/questions/${question.test_type}/${questionId}`, {
      method: "PUT",
      body: JSON.stringify(question),
    }),

  // Delete a question
  delete: (questionId, testType) =>
    apiCall(`/questions/${testType}/${questionId}`, {
      method: "DELETE",
    }),
};

// Content API (placeholder for future)
export const contentAPI = {
  // Get custom content
  get: () => apiCall("/content.php"),

  // Update custom content
  update: (content) =>
    apiCall("/content.php", {
      method: "PUT",
      body: JSON.stringify(content),
    }),
};

// SEO API
export const seoAPI = {
  // Get SEO settings
  getSettings: () => apiCall("/seo-settings"),

  // Update SEO settings
  updateSettings: (settings) =>
    apiCall("/seo-settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    }),
};

// Custom Content API
export const customContentAPI = {
  // Get custom content
  get: () => apiCall("/custom-content"),

  // Update custom content (with optional activityType header for logging)
  update: (content, activityType = null) =>
    apiCall("/custom-content", {
      method: "PUT",
      body: JSON.stringify(content),
      headers: {
        'Content-Type': 'application/json',
        ...(activityType && { 'X-Activity-Type': activityType })
      }
    }),

  // Get template activity log
  getActivityLog: (testType, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString();
    return apiCall(`/custom-content/template-activity-log/${testType}${queryString ? '?' + queryString : ''}`);
  },
};

// Test Passwords API
export const testPasswordsAPI = {
  // Get test passwords
  get: () => apiCall("/test-passwords"),

  // Update test passwords
  update: (passwords) =>
    apiCall("/test-passwords", {
      method: "PUT",
      body: JSON.stringify(passwords),
    }),
};

// Template Password API - Get password for active template
// ⚠️ DEPRECATED: Nên dùng verifyPasswordAPI thay thế để bảo mật
export const templatePasswordAPI = {
  // Get password for active template (with fallback to test password)
  get: (testType) => apiCall(`/template-password/${testType}`),
};

// Verify Password API - Verify password securely on server (KHÔNG trả về password)
export const verifyPasswordAPI = {
  // Verify password - Server so sánh và chỉ trả về success/failed
  verify: (testType, password) =>
    apiCall("/verify-password", {
      method: "POST",
      body: JSON.stringify({ testType, password }),
    }),
};

// Template API - Save individual template (Phase 2B optimization)
export const templateAPI = {
  // Update a single template
  update: (testType, templateId, templateData) =>
    apiCall(`/template/${testType}/${templateId}`, {
      method: "PUT",
      body: JSON.stringify(templateData),
    }),
};

// Header Footer Settings API
export const headerFooterAPI = {
  // Get header footer settings
  get: () => apiCall("/header-footer-settings"),

  // Update header footer settings
  update: (settings) =>
    apiCall("/header-footer-settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    }),
};

// Admin Password API
export const adminPasswordAPI = {
  // Get admin password
  get: () => apiCall("/admin-password"),

  // Update admin password
  update: (password) =>
    apiCall("/admin-password", {
      method: "PUT",
      body: JSON.stringify({ password }),
    }),
};

// Backup/Restore API
export const backupAPI = {
  // Create backup
  create: () => apiCall("/backup.php"),

  // Restore from backup
  restore: (backupData) =>
    apiCall("/restore.php", {
      method: "POST",
      body: JSON.stringify(backupData),
    }),
};

// Migration helpers for moving from localStorage to API
export const migrationAPI = {
  // Migrate localStorage data to API
  migrateFromLocalStorage: async () => {
    try {
      const testTypes = ["mbti", "iq", "eq", "disc", "holland"];
      const migrationResults = {
        questions: {},
        content: null,
        errors: [],
      };

      // Migrate questions
      for (const testType of testTypes) {
        try {
          const localQuestions = localStorage.getItem(`${testType}_questions`);
          if (localQuestions) {
            const questions = JSON.parse(localQuestions);
            // Add each question individually since we have them in database already
            let successCount = 0;
            for (const question of questions) {
              try {
                await questionsAPI.add(testType, question);
                successCount++;
              } catch (error) {
                console.warn(`Failed to add question to ${testType}:`, error);
              }
            }
            migrationResults.questions[testType] = successCount;
          }
        } catch (error) {
          migrationResults.errors.push(
            `Failed to migrate ${testType} questions: ${error.message}`
          );
        }
      }

      return migrationResults;
    } catch (error) {
      throw new Error(`Migration failed: ${error.message}`);
    }
  },

  // Check if API is available
  checkAPIHealth: async () => {
    try {
      const response = await questionsAPI.getByType("mbti");
      return Array.isArray(response) && response.length > 0;
    } catch (error) {
      return false;
    }
  },

  // Get localStorage data for comparison
  getLocalStorageData: () => {
    const testTypes = ["mbti", "iq", "eq", "disc", "holland"];
    const data = {
      questions: {},
      customContent: {},
    };

    testTypes.forEach((testType) => {
      const questions = localStorage.getItem(`${testType}_questions`);
      if (questions) {
        try {
          data.questions[testType] = JSON.parse(questions);
        } catch (e) {
          console.warn(
            `Failed to parse ${testType} questions from localStorage`
          );
        }
      }
    });

    const content = localStorage.getItem("custom_content");
    if (content) {
      try {
        data.customContent = JSON.parse(content);
      } catch (e) {
        console.warn("Failed to parse custom content from localStorage");
      }
    }

    return data;
  },
};

// Tracking API
export const trackingAPI = {
  // Track visitor (before password verification)
  trackVisitor: (testType, referrerSource, utmParams = {}) => {
    const { utm_source, utm_medium, utm_campaign, gclid } = utmParams;
    return apiCall("/track-visitor", {
      method: "POST",
      body: JSON.stringify({
        testType,
        referrerSource,
        utmSource: utm_source,
        utmMedium: utm_medium,
        utmCampaign: utm_campaign,
        gclid,
      }),
    });
  },

  // Track password verification
  trackPasswordVerification: (testType, referrerSource, utmParams = {}) => {
    const { utm_source, utm_medium, utm_campaign, gclid } = utmParams;
    return apiCall("/track-password-verification", {
      method: "POST",
      body: JSON.stringify({
        testType,
        referrerSource,
        utmSource: utm_source,
        utmMedium: utm_medium,
        utmCampaign: utm_campaign,
        gclid,
      }),
    });
  },

  // Get statistics
  getStatistics: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/statistics/password-verifications${queryString ? `?${queryString}` : ''}`);
  },

  // Get visitor statistics
  getVisitorStatistics: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/statistics/visitors${queryString ? `?${queryString}` : ''}`);
  },
};

// Auto-rotate helpers
export const autoRotateAPI = {
  getCurrentCount: (testType) => {
    const query = new URLSearchParams({ testType }).toString();
    return apiCall(`/auto-rotate/current-count?${query}`);
  },
};
// Helper function to extract UTM parameters from URL
export const getUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    gclid: urlParams.get('gclid'),
  };
};

// Health check
export const healthCheck = () => questionsAPI.getByType("mbti");

export default {
  questionsAPI,
  contentAPI,
  seoAPI,
  customContentAPI,
  testPasswordsAPI,
  templatePasswordAPI,
  verifyPasswordAPI,
  templateAPI,
  headerFooterAPI,
  adminPasswordAPI,
  backupAPI,
  migrationAPI,
  trackingAPI,
  autoRotateAPI,
  healthCheck,
  getUTMParams,
};
